import React from "react";

const S = {
  wrapper: { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — The Limit Definition of the Derivative
// ─────────────────────────────────────────────────────────────────
function DiagramLimitDefinition() {
  const W = 900, H = 340;
  const ox = 80, oy = 260, scX = 110, scY = 90;
  const toX = x => ox + x * scX;
  const toY = y => oy - y * scY;

  // f(x) = x^2, tangent at x=1.5
  const f = x => x * x;
  const a = 1.5, fa = f(a);
  const fprime = 2 * a; // true derivative
  const h = 0.9;        // secant point
  const b = a + h, fb = f(b);
  const secSlope = (fb - fa) / h;

  // curve points
  const curvePts = [];
  for (let x = 0.0; x <= 3.0; x += 0.04) {
    curvePts.push(`${toX(x).toFixed(1)},${toY(f(x)).toFixed(1)}`);
  }

  // tangent line at a: y - fa = fprime*(x - a)
  const tanY = x => fa + fprime * (x - a);
  // secant line
  const secY = x => fa + secSlope * (x - a);

  // Panel x offset for formula panel
  const panX = 560, panW = W - panX - 18;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Limit Definition of the Derivative — Secant to Tangent</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {/* Grid */}
        {[0,1,2,3].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={toY(0)} x2={toX(v)} y2={toY(4.2)} stroke="#eee" strokeWidth="1"/>
            <line x1={toX(0)} y1={toY(v)} x2={toX(3.2)} y2={toY(v)} stroke="#eee" strokeWidth="1"/>
          </g>
        ))}

        {/* Axes */}
        <line x1={toX(-0.2)} y1={oy} x2={toX(3.2)} y2={oy} stroke="#1F3864" strokeWidth="1.8"/>
        <line x1={ox} y1={toY(4.3)} x2={ox} y2={toY(-0.3)} stroke="#1F3864" strokeWidth="1.8"/>
        <polygon points={`${toX(3.2)},${oy} ${toX(3.2)-8},${oy-4} ${toX(3.2)-8},${oy+4}`} fill="#1F3864"/>
        <polygon points={`${ox},${toY(4.3)} ${ox-4},${toY(4.3)+8} ${ox+4},${toY(4.3)+8}`} fill="#1F3864"/>
        <text x={toX(3.2)+6} y={oy+5} fontSize="15" fill="#1F3864" fontWeight="700">x</text>
        <text x={ox+5} y={toY(4.3)-4} fontSize="15" fill="#1F3864" fontWeight="700">y</text>

        {/* Tick labels */}
        {[1,2,3].map(v => (
          <g key={v}>
            <text x={toX(v)} y={oy+18} textAnchor="middle" fontSize="12" fill="#aaa">{v}</text>
          </g>
        ))}
        {[1,2,3,4].map(v => (
          <text key={v} x={ox-8} y={toY(v)+4} textAnchor="end" fontSize="12" fill="#aaa">{v}</text>
        ))}

        {/* f(x) = x² curve */}
        <polyline points={curvePts.join(' ')} fill="none" stroke="#2E75B6" strokeWidth="3"/>
        <text x={toX(2.72)} y={toY(f(2.72))-12} fontSize="13" fill="#2E75B6" fontWeight="700">f(x) = x²</text>

        {/* Secant line */}
        <line x1={toX(0.4)} y1={toY(secY(0.4))} x2={toX(3.0)} y2={toY(secY(3.0))} stroke="#9b59b6" strokeWidth="2" strokeDasharray="8,4"/>

        {/* Tangent line */}
        <line x1={toX(0.3)} y1={toY(tanY(0.3))} x2={toX(2.8)} y2={toY(tanY(2.8))} stroke="#c0392b" strokeWidth="2.5"/>

        {/* Point A = (a, f(a)) */}
        <circle cx={toX(a)} cy={toY(fa)} r={6} fill="#c0392b" stroke="#fff" strokeWidth="2"/>
        <text x={toX(a)-34} y={toY(fa)-10} fontSize="13" fill="#c0392b" fontWeight="700">A(a, f(a))</text>

        {/* Point B = (a+h, f(a+h)) */}
        <circle cx={toX(b)} cy={toY(fb)} r={6} fill="#9b59b6" stroke="#fff" strokeWidth="2"/>
        <text x={toX(b)+8} y={toY(fb)-10} fontSize="13" fill="#9b59b6" fontWeight="700">B(a+h, f(a+h))</text>

        {/* Δx and Δy legs */}
        <line x1={toX(a)} y1={toY(fa)} x2={toX(b)} y2={toY(fa)} stroke="#e07b00" strokeWidth="1.8" strokeDasharray="5,3"/>
        <line x1={toX(b)} y1={toY(fa)} x2={toX(b)} y2={toY(fb)} stroke="#27ae60" strokeWidth="1.8" strokeDasharray="5,3"/>
        <text x={(toX(a)+toX(b))/2} y={toY(fa)+18} textAnchor="middle" fontSize="12" fill="#e07b00" fontWeight="700">h = Δx</text>
        <text x={toX(b)+14} y={(toY(fa)+toY(fb))/2+4} fontSize="12" fill="#27ae60" fontWeight="700">Δy</text>

        {/* Secant label */}
        <text x={toX(2.6)} y={toY(secY(2.6))+18} fontSize="11" fill="#9b59b6" fontWeight="700">secant</text>

        {/* Tangent label */}
        <text x={toX(2.5)} y={toY(tanY(2.5))-10} fontSize="11" fill="#c0392b" fontWeight="700">tangent (h→0)</text>

        {/* Right panel: limit definition */}
        <rect x={panX} y={10} width={panW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={32} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">Limit Definition</text>

        {[
          {y:40,  bg:"#EBF3FB", bc:"#2E75B6", title:"Derivative at a point",
           lines:["f'(a) = lim  [f(a+h)−f(a)] / h","        h→0"]},
          {y:116, bg:"#fff7ee", bc:"#e07b00", title:"Secant slope (finite h)",
           lines:["slope = [f(a+h)−f(a)] / h","= Δy / Δx  (rise over run)"]},
          {y:192, bg:"#f0fff4", bc:"#27ae60", title:"As h → 0 ...",
           lines:["Secant → Tangent","Avg rate → Instant. rate"]},
          {y:268, bg:"#fff0f0", bc:"#c0392b", title:"Derivative function",
           lines:["f'(x) = lim  [f(x+h)−f(x)] / h","        h→0"]},
        ].map(({y,bg,bc,title,lines}) => (
          <g key={title}>
            <rect x={panX+8} y={y} width={panW-16} height={70} rx={3} fill={bg} stroke={bc} strokeWidth="1.2"/>
            <text x={panX+16} y={y+16} fontSize="12" fill="#1F3864" fontWeight="700">{title}</text>
            {lines.map((ln,i) => (
              <text key={i} x={panX+14} y={y+32+i*18} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The derivative is the limit of the secant slope as h→0. The secant line through A and B has slope Δy/Δx = [f(a+h)−f(a)]/h. As h shrinks to zero, B slides toward A and the secant rotates to become the tangent. That limiting slope is f'(a).
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Basic Differentiation Rules
// ─────────────────────────────────────────────────────────────────
function DiagramBasicRules() {
  const W = 900, H = 320;
  const rules = [
    { name:"Power Rule",    formula:"d/dx [xⁿ] = n·xⁿ⁻¹",          example:"d/dx [x⁴] = 4x³",          color:"#2E75B6", fill:"#EBF3FB",  note:"Most-used rule. Bring exponent down, reduce by 1." },
    { name:"Constant",      formula:"d/dx [c] = 0",                   example:"d/dx [7] = 0",              color:"#27ae60", fill:"#f0fff4",  note:"Constants have zero slope — they don't change." },
    { name:"Constant Mult", formula:"d/dx [c·f] = c·f'",             example:"d/dx [5x³] = 15x²",        color:"#e07b00", fill:"#fff7ee",  note:"Constants factor out of the derivative." },
    { name:"Sum / Diff",    formula:"d/dx [f±g] = f'±g'",            example:"d/dx [x²+3x] = 2x+3",     color:"#9b59b6", fill:"#f5d0ff",  note:"Differentiate term by term." },
    { name:"Product Rule",  formula:"d/dx [f·g] = f'g + fg'",        example:"d/dx [x²·sin x]\n= 2x·sin x + x²·cos x", color:"#c0392b", fill:"#fff0f0", note:"'First times d-second plus second times d-first'." },
  ];

  const panW = (W - 20) / rules.length - 6;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Basic Differentiation Rules — Power, Constant, Product, Sum</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {rules.map(({name,formula,example,color,fill,note},i) => {
          const x = 10 + i * (panW + 6);
          return (
            <g key={name}>
              <rect x={x} y={10} width={panW} height={H-20} rx={6} fill={fill} stroke={color} strokeWidth="1.8"/>
              <rect x={x+4} y={14} width={panW-8} height={32} rx={4} fill={color}/>
              <text x={x+panW/2} y={35} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{name}</text>
              {/* Formula box */}
              <rect x={x+6} y={56} width={panW-12} height={34} rx={3} fill="#fff" stroke={color} strokeWidth="1"/>
              <text x={x+panW/2} y={68} textAnchor="middle" fontSize="10" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{formula.split('\n')[0]}</text>
              {formula.split('\n')[1] && <text x={x+panW/2} y={82} textAnchor="middle" fontSize="10" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{formula.split('\n')[1]}</text>}
              {/* Example box */}
              <rect x={x+6} y={100} width={panW-12} height={44} rx={3} fill={color} fillOpacity="0.12"/>
              <text x={x+panW/2} y={116} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">Example:</text>
              {example.split('\n').map((ln,j) => (
                <text key={j} x={x+panW/2} y={130+j*14} textAnchor="middle" fontSize="10" fill="#333" fontFamily="'Courier New',monospace">{ln}</text>
              ))}
              {/* Note */}
              <line x1={x+12} y1={155} x2={x+panW-12} y2={155} stroke="#ddd" strokeWidth="1"/>
              {note.split(' ').reduce((acc,word) => {
                // wrap into ~25-char lines
                if (!acc.length || (acc[acc.length-1]+' '+word).length > 22) acc.push(word);
                else acc[acc.length-1] += ' '+word;
                return acc;
              }, []).map((ln,j) => (
                <text key={j} x={x+panW/2} y={172+j*15} textAnchor="middle" fontSize="10" fill="#555">{ln}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        The five foundational rules cover most polynomial and algebraic derivatives. The power rule is used on every term individually; sum/difference lets you differentiate term-by-term; the product rule handles two functions multiplied together.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Chain Rule and Quotient Rule
// ─────────────────────────────────────────────────────────────────
function DiagramChainQuotient() {
  const W = 900, H = 330;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Chain Rule and Quotient Rule — Composite and Rational Functions</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* ── Chain Rule panel ── */}
        <rect x={10} y={10} width={430} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={14} width={422} height={36} rx={4} fill="#2E75B6"/>
        <text x={225} y={37} textAnchor="middle" fontSize="15" fill="#fff" fontWeight="700">Chain Rule  —  Composite Functions</text>

        {/* Main formula */}
        <rect x={22} y={60} width={414} height={36} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={229} y={83} textAnchor="middle" fontSize="14" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">d/dx [f(g(x))] = f'(g(x)) · g'(x)</text>

        {/* Anatomy */}
        <text x={30} y={116} fontSize="12" fill="#1F3864" fontWeight="700">How to read it:</text>
        <rect x={22} y={122} width={414} height={76} rx={3} fill="#f8fbff" stroke="#ccd9ea"/>
        {[
          {y:140, color:'#c0392b', label:'f(g(x))', desc:'the OUTER function evaluated at the inner'},
          {y:158, color:'#27ae60', label:"f'(g(x))", desc:'derivative of OUTER (keeping inner intact)'},
          {y:176, color:'#e07b00', label:"g'(x)",   desc:'derivative of INNER function'},
          {y:194, color:'#9b59b6', label:'·  (multiply)', desc:'multiply the two derivatives together'},
        ].map(({y,color,label,desc}) => (
          <g key={label}>
            <text x={32} y={y} fontSize="12" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{label}</text>
            <text x={138} y={y} fontSize="11" fill="#555">{desc}</text>
          </g>
        ))}

        {/* Worked example */}
        <text x={30} y={222} fontSize="12" fill="#1F3864" fontWeight="700">Worked Example:  f(x) = sin(x²)</text>
        {[
          {y:240, txt:'Outer f(u) = sin(u)   →   f\'(u) = cos(u)', color:'#27ae60'},
          {y:258, txt:'Inner g(x) = x²        →   g\'(x) = 2x',  color:'#e07b00'},
          {y:276, txt:'Result: d/dx[sin(x²)] = cos(x²) · 2x',    color:'#2E75B6'},
        ].map(({y,txt,color}) => (
          <text key={y} x={30} y={y} fontSize="12" fill={color} fontFamily="'Courier New',monospace">{txt}</text>
        ))}
        <text x={30} y={306} fontSize="11" fill="#888" fontStyle="italic">Steps: identify outer/inner → differentiate each → multiply</text>

        {/* ── Quotient Rule panel ── */}
        <rect x={454} y={10} width={436} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={458} y={14} width={428} height={36} rx={4} fill="#c0392b"/>
        <text x={672} y={37} textAnchor="middle" fontSize="15" fill="#fff" fontWeight="700">Quotient Rule  —  f(x) / g(x)</text>

        {/* Main formula */}
        <rect x={462} y={60} width={420} height={36} rx={4} fill="#fff0f0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={672} y={83} textAnchor="middle" fontSize="14" fill="#c0392b" fontFamily="'Courier New',monospace" fontWeight="700">d/dx [f/g] = (f'g − fg') / g²</text>

        {/* Mnemonic */}
        <rect x={462} y={106} width={420} height={46} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
        <text x={672} y={124} textAnchor="middle" fontSize="12" fill="#e07b00" fontWeight="700">Mnemonic: "Low d-High minus High d-Low, over Low squared"</text>
        <text x={672} y={142} textAnchor="middle" fontSize="11" fill="#888" fontStyle="italic">Low = g(x),  High = f(x),  d-High = f'(x),  d-Low = g'(x)</text>

        {/* Anatomy */}
        <rect x={462} y={162} width={420} height={72} rx={3} fill="#f8fbff" stroke="#ccd9ea"/>
        {[
          {y:180, color:'#27ae60', label:"f'(x)·g(x)", desc:"derivative of numerator × denominator"},
          {y:198, color:'#c0392b', label:"f(x)·g'(x)", desc:"numerator × derivative of denominator"},
          {y:216, color:'#1F3864', label:"[g(x)]²",    desc:"square of the denominator"},
          {y:228, color:'#9b59b6', label:"minus sign",  desc:"always subtract, never add"},
        ].map(({y,color,label,desc}) => (
          <g key={label}>
            <text x={472} y={y} fontSize="12" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{label}</text>
            <text x={590} y={y} fontSize="11" fill="#555">{desc}</text>
          </g>
        ))}

        {/* Worked example */}
        <text x={470} y={258} fontSize="12" fill="#1F3864" fontWeight="700">Worked Example:  f(x) = x² / sin(x)</text>
        {[
          {y:276, txt:"f=x², f'=2x;   g=sin x, g'=cos x",         color:'#27ae60'},
          {y:294, txt:"= (2x·sin x − x²·cos x) / sin²x",           color:'#c0392b'},
        ].map(({y,txt,color}) => (
          <text key={y} x={470} y={y} fontSize="12" fill={color} fontFamily="'Courier New',monospace">{txt}</text>
        ))}
        <text x={470} y={316} fontSize="11" fill="#888" fontStyle="italic">Simplify only after applying the rule correctly</text>
      </svg>
      <div style={S.dCaption}>
        The chain rule handles any composition f(g(x)) — always work outside-in. The quotient rule handles f/g — a common error is reversing the minus sign. Both rules can be combined with each other and with the product rule for complex expressions.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Derivatives of Standard Functions
// ─────────────────────────────────────────────────────────────────
function DiagramStandardDerivatives() {
  const W = 900, H = 340;
  const ox = 75, oy = 200, scX = 95, scY = 75;
  const toX = x => ox + x * scX;
  const toY = y => oy - y * scY;

  const curves = [
    { label:'sin x', fn: x => Math.sin(x), dfn: x => Math.cos(x), color:'#2E75B6', dcolor:'#9b59b6', dlabel:"cos x" },
    { label:'eˣ',    fn: x => Math.exp(x), dfn: x => Math.exp(x), color:'#c0392b', dcolor:'#e07b00', dlabel:"eˣ (same!)" },
  ];

  // build path for sin and cos over [-0.2, 2π+0.2]
  function makePath(fn, x0, x1, step) {
    const pts = [];
    for (let x = x0; x <= x1; x += step) {
      const sx = toX(x), sy = toY(fn(x));
      if (sy > 5 && sy < H-10) pts.push(`${sx.toFixed(1)},${sy.toFixed(1)}`);
    }
    return 'M ' + pts.join(' L ');
  }

  const sinPath  = makePath(x => Math.sin(x), -0.1, 2*Math.PI+0.1, 0.04);
  const cosPath  = makePath(x => Math.cos(x), -0.1, 2*Math.PI+0.1, 0.04);

  // Panel 2: trig derivatives table (right side)
  const panX = 480, panW = W - panX - 16;

  const piTicks = [[0,'0'],[Math.PI/2,'π/2'],[Math.PI,'π'],[3*Math.PI/2,'3π/2'],[2*Math.PI,'2π']];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Derivatives of Standard Functions — Trig, Exponential, Logarithm</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Graph panel */}
        <rect x={10} y={10} width={460} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={240} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">d/dx[sin x] = cos x  —  a function is its own derivative's shift</text>

        {/* Grid */}
        {[0,1,2,3,4,5,6].map(v => (
          <line key={v} x1={toX(v*Math.PI/2)} y1={toY(-1.4)} x2={toX(v*Math.PI/2)} y2={toY(1.4)} stroke="#f0f0f0" strokeWidth="1"/>
        ))}
        {[-1,0,1].map(v => (
          <line key={v} x1={toX(-0.1)} y1={toY(v)} x2={toX(2*Math.PI+0.1)} y2={toY(v)} stroke="#f0f0f0" strokeWidth="1"/>
        ))}

        {/* Axes */}
        <line x1={toX(-0.1)} y1={oy} x2={toX(2*Math.PI+0.2)} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
        <line x1={ox} y1={toY(1.5)} x2={ox} y2={toY(-1.5)} stroke="#1F3864" strokeWidth="1.5"/>
        <text x={toX(2*Math.PI+0.2)+4} y={oy+4} fontSize="12" fill="#1F3864">x</text>
        <text x={ox+4} y={toY(1.5)-4} fontSize="12" fill="#1F3864">y</text>

        {/* y ticks */}
        {[-1,1].map(v => (
          <g key={v}>
            <line x1={ox-4} y1={toY(v)} x2={ox+4} y2={toY(v)} stroke="#1F3864" strokeWidth="1.2"/>
            <text x={ox-8} y={toY(v)+4} textAnchor="end" fontSize="11" fill="#aaa">{v}</text>
          </g>
        ))}

        {/* x ticks */}
        {piTicks.map(([t,lbl]) => (
          <g key={lbl}>
            <line x1={toX(t)} y1={oy-4} x2={toX(t)} y2={oy+4} stroke="#1F3864" strokeWidth="1.2"/>
            <text x={toX(t)} y={oy+16} textAnchor="middle" fontSize="11" fill="#aaa">{lbl}</text>
          </g>
        ))}

        {/* sin x */}
        <path d={sinPath} fill="none" stroke="#2E75B6" strokeWidth="3"/>
        <text x={toX(Math.PI/2)+6} y={toY(1.0)-8} fontSize="13" fill="#2E75B6" fontWeight="700">sin x</text>

        {/* cos x */}
        <path d={cosPath} fill="none" stroke="#c0392b" strokeWidth="2.5" strokeDasharray="8,4"/>
        <text x={toX(0.1)} y={toY(1.02)-8} fontSize="13" fill="#c0392b" fontWeight="700">cos x = (sin x)'</text>

        {/* Annotation: slope of sin at x=0 equals cos(0)=1 */}
        <circle cx={toX(0)} cy={toY(0)} r={5} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
        <line x1={toX(-0.2)} y1={toY(-0.2)} x2={toX(0.5)} y2={toY(0.5)} stroke="#27ae60" strokeWidth="2"/>
        <text x={toX(0.5)+6} y={toY(0.5)+4} fontSize="11" fill="#27ae60" fontWeight="700">slope=1=cos(0)</text>

        {/* Annotation: slope of sin at π/2 = 0 */}
        <circle cx={toX(Math.PI/2)} cy={toY(1)} r={5} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
        <line x1={toX(Math.PI/2-0.4)} y1={toY(Math.sin(Math.PI/2-0.4))} x2={toX(Math.PI/2+0.4)} y2={toY(Math.sin(Math.PI/2+0.4))} stroke="#27ae60" strokeWidth="2"/>
        <text x={toX(Math.PI/2)+8} y={toY(1)+14} fontSize="11" fill="#27ae60" fontWeight="700">slope=0=cos(π/2)</text>

        {/* Right panel: table of standard derivatives */}
        <rect x={panX} y={10} width={panW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Standard Derivatives</text>

        {[
          ['d/dx [xⁿ]',    'n·xⁿ⁻¹',        '#2E75B6'],
          ['d/dx [eˣ]',    'eˣ',              '#c0392b'],
          ['d/dx [aˣ]',    'aˣ · ln(a)',      '#c0392b'],
          ['d/dx [ln x]',  '1/x',             '#27ae60'],
          ['d/dx [log_a x]','1/(x·ln a)',      '#27ae60'],
          ['d/dx [sin x]', 'cos x',           '#9b59b6'],
          ['d/dx [cos x]', '−sin x',          '#9b59b6'],
          ['d/dx [tan x]', 'sec² x',          '#e07b00'],
          ['d/dx [cot x]', '−csc² x',         '#e07b00'],
          ['d/dx [sec x]', 'sec x · tan x',   '#555'],
          ['d/dx [csc x]', '−csc x · cot x',  '#555'],
        ].map(([fn, deriv, color], i) => (
          <g key={fn}>
            <rect x={panX+6} y={40+i*26} width={panW-12} height={24} rx={2} fill={i%2===0?'#f8fbff':'#fff'}/>
            <text x={panX+14} y={56+i*26} fontSize="11" fill="#1F3864" fontFamily="'Courier New',monospace" fontWeight="700">{fn}</text>
            <text x={panX+panW-14} y={56+i*26} textAnchor="end" fontSize="11" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">→ {deriv}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The graph shows that d/dx[sin x] = cos x directly: the slope of sin at any point equals the value of cos at that same point. The table lists all standard derivatives to memorize — eˣ is its own derivative, and ln x differentiates to 1/x.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Geometric Meaning: Tangent Lines & Curve Sketching
// ─────────────────────────────────────────────────────────────────
function DiagramGeometricMeaning() {
  const W = 900, H = 340;
  const ox = 70, oy = 220, scX = 70, scY = 40;
  const toX = x => ox + (x + 3) * scX;
  const toY = y => oy - y * scY;

  // f(x) = x³ - 3x + 1
  const f  = x => x*x*x - 3*x + 1;
  const fp = x => 3*x*x - 3;   // f'

  const curvePts = [];
  for (let x = -2.6; x <= 2.6; x += 0.04) {
    const sy = toY(f(x));
    if (sy > 5 && sy < H - 5) curvePts.push(`${toX(x).toFixed(1)},${sy.toFixed(1)}`);
  }

  const criticals = [-1, 1]; // f'=0 at x=-1,1

  // tangent at key points
  const tanLine = (a, len=1.4) => {
    const m = fp(a), fa = f(a);
    return {
      x1: toX(a-len), y1: toY(fa + m*(-len)),
      x2: toX(a+len), y2: toY(fa + m*(len)),
    };
  };

  const panX = 530, panW = W - panX - 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Geometric Meaning of the Derivative — Tangents, Critical Points, Increasing/Decreasing</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {/* Graph background */}
        <rect x={10} y={10} width={510} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={265} y={30} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">f(x) = x³ − 3x + 1   and   f'(x) = 3x² − 3</text>

        {/* Grid */}
        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={toY(-4)} x2={toX(v)} y2={toY(6)} stroke="#f0f0f0" strokeWidth="1"/>
            <line x1={toX(-2.6)} y1={toY(v*2)} x2={toX(2.6)} y2={toY(v*2)} stroke="#f0f0f0" strokeWidth="1"/>
          </g>
        ))}

        {/* Axes */}
        <line x1={toX(-2.8)} y1={oy} x2={toX(2.8)} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
        <line x1={ox} y1={toY(6)} x2={ox} y2={toY(-4)} stroke="#1F3864" strokeWidth="1.5"/>
        <text x={toX(2.8)+4} y={oy+4} fontSize="12" fill="#1F3864">x</text>

        {/* Tick labels */}
        {[-2,-1,1,2].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={oy-3} x2={toX(v)} y2={oy+3} stroke="#1F3864" strokeWidth="1.2"/>
            <text x={toX(v)} y={oy+16} textAnchor="middle" fontSize="11" fill="#aaa">{v}</text>
          </g>
        ))}

        {/* f(x) curve */}
        <polyline points={curvePts.join(' ')} fill="none" stroke="#2E75B6" strokeWidth="3"/>
        <text x={toX(2.1)} y={toY(f(2.1))-10} fontSize="13" fill="#2E75B6" fontWeight="700">f(x)</text>

        {/* f'(x) curve */}
        {(() => {
          const dPts = [];
          for (let x = -2.4; x <= 2.4; x += 0.04) {
            const sy = toY(fp(x));
            if (sy > 14 && sy < H-14) dPts.push(`${toX(x).toFixed(1)},${sy.toFixed(1)}`);
          }
          return <polyline points={dPts.join(' ')} fill="none" stroke="#9b59b6" strokeWidth="2.2" strokeDasharray="8,4"/>;
        })()}
        <text x={toX(2.2)} y={toY(fp(2.2))-10} fontSize="13" fill="#9b59b6" fontWeight="700">f'(x)</text>

        {/* Tangent at local max x=-1 (horizontal tangent) */}
        {(() => {
          const t = tanLine(-1, 1.0);
          return (
            <g>
              <line {...t} stroke="#e07b00" strokeWidth="2.2"/>
              <circle cx={toX(-1)} cy={toY(f(-1))} r={6} fill="#e07b00" stroke="#fff" strokeWidth="2"/>
              <text x={toX(-1)-12} y={toY(f(-1))-12} fontSize="11" fill="#e07b00" fontWeight="700">local max</text>
              <text x={toX(-1)-14} y={toY(f(-1))-26} fontSize="10" fill="#e07b00">f'=0 here</text>
            </g>
          );
        })()}

        {/* Tangent at local min x=1 (horizontal tangent) */}
        {(() => {
          const t = tanLine(1, 1.0);
          return (
            <g>
              <line {...t} stroke="#27ae60" strokeWidth="2.2"/>
              <circle cx={toX(1)} cy={toY(f(1))} r={6} fill="#27ae60" stroke="#fff" strokeWidth="2"/>
              <text x={toX(1)+8} y={toY(f(1))+16} fontSize="11" fill="#27ae60" fontWeight="700">local min</text>
              <text x={toX(1)+8} y={toY(f(1))+30} fontSize="10" fill="#27ae60">f'=0 here</text>
            </g>
          );
        })()}

        {/* Tangent at x=-2 (steep negative slope) */}
        {(() => {
          const t = tanLine(-2, 0.6);
          return (
            <g>
              <line {...t} stroke="#c0392b" strokeWidth="2"/>
              <circle cx={toX(-2)} cy={toY(f(-2))} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
              <text x={toX(-2)-10} y={toY(f(-2))+18} fontSize="10" fill="#c0392b"> {"f' > 0 (rising)"}</text>
            </g>
          );
        })()}

        {/* Tangent at x=0 (negative slope) */}
        {(() => {
          const t = tanLine(0, 0.7);
          return (
            <g>
              <line {...t} stroke="#c0392b" strokeWidth="2"/>
              <circle cx={toX(0)} cy={toY(f(0))} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
              <text x={toX(0)+8} y={toY(f(0))+4} fontSize="10" fill="#c0392b">f'&lt;0 (falling)</text>
            </g>
          );
        })()}

        {/* Zero crossings of f' */}
        {[-1,1].map(x => (
          <g key={x}>
            <circle cx={toX(x)} cy={toY(fp(x))} r={5} fill="#9b59b6" stroke="#fff" strokeWidth="1.5"/>
          </g>
        ))}
        <text x={toX(-2.5)} y={toY(fp(-2.5))+14} fontSize="10" fill="#9b59b6">f'(x)=0 at x=±1</text>

        {/* Right panel */}
        <rect x={panX} y={10} width={panW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={30} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Key Principles</text>

        {[
          {y:40,  bg:'#EBF3FB', bc:'#2E75B6', lines:["f'(x) > 0", "f is increasing", "tangent slopes up"]},
          {y:110, bg:'#fff0f0', bc:'#c0392b', lines:["f'(x) < 0", "f is decreasing", "tangent slopes down"]},
          {y:180, bg:'#fff7ee', bc:'#e07b00', lines:["f'(x) = 0", "critical point", "possible max/min"]},
          {y:250, bg:'#f0fff4', bc:'#27ae60', lines:["f''(x) > 0", "concave up (∪)", "local min if f'=0"]},
        ].map(({y,bg,bc,lines}) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={64} rx={3} fill={bg} stroke={bc} strokeWidth="1.2"/>
            {lines.map((ln,i) => (
              <text key={i} x={panX+16} y={y+18+i*17} fontSize="12"
                fill={i===0 ? bc : i===1 ? '#1F3864' : '#555'}
                fontWeight={i===0?'700':'400'}
                fontFamily={i===0?"'Courier New',monospace":"'Georgia',serif"}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The sign of f'(x) tells you whether f is rising or falling. Where f'=0, the tangent is horizontal — these are critical points (local max or min). The dashed purple curve is f'(x); its zero crossings align exactly with the peaks and valleys of f(x).
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Implicit Differentiation & Related Rates Overview
// ─────────────────────────────────────────────────────────────────
function DiagramImplicitRelated() {
  const W = 900, H = 320;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Implicit Differentiation and Related Rates — Key Techniques</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Header bar */}
        <rect x={14} y={10} width={W-28} height={42} rx={5} fill="#1F3864"/>
        <text x={W/2} y={28} textAnchor="middle" fontSize="15" fill="#fff" fontWeight="700">When y is not isolated — differentiate both sides with respect to x, treating y as a function of x</text>
        <text x={W/2} y={46} textAnchor="middle" fontSize="12" fill="#aac8e8">Whenever you differentiate a term with y, multiply by dy/dx (chain rule applied implicitly)</text>

        {/* Implicit differentiation panel */}
        {(() => {
          const x=14, w=430, h=240;
          return (
            <g>
              <rect x={x} y={62} width={w} height={h} rx={5} fill="#fff" stroke="#ccd9ea"/>
              <rect x={x+4} y={66} width={w-8} height={26} rx={3} fill="#2E75B6"/>
              <text x={x+w/2} y={83} textAnchor="middle" fontSize="13" fill="#fff" fontWeight="700">Implicit Differentiation</text>

              {/* Steps */}
              {[
                {y:104, step:'1', txt:'Write the equation as-is (do not solve for y)', color:'#1F3864'},
                {y:128, step:'2', txt:'Differentiate both sides w.r.t. x', color:'#1F3864'},
                {y:152, step:'3', txt:'For every y term: apply dy/dx (chain rule)', color:'#e07b00'},
                {y:176, step:'4', txt:'Collect all dy/dx terms on one side', color:'#2E75B6'},
                {y:200, step:'5', txt:'Factor out and solve for dy/dx', color:'#27ae60'},
              ].map(({y,step,txt,color}) => (
                <g key={step}>
                  <circle cx={x+22} cy={y-5} r={10} fill={color}/>
                  <text x={x+22} y={y-1} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{step}</text>
                  <text x={x+40} y={y} fontSize="12" fill="#333">{txt}</text>
                </g>
              ))}

              {/* Example: circle x²+y²=25 */}
              <rect x={x+10} y={216} width={w-20} height={78} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
              <text x={x+18} y={232} fontSize="11" fill="#1F3864" fontWeight="700">Example:  x² + y² = 25  (circle, r=5)</text>
              {[
                "Diff: 2x + 2y·(dy/dx) = 0",
                "Solve: dy/dx = −x / y",
                "At (3,4):  dy/dx = −3/4  (slope of tangent)",
              ].map((ln,i) => (
                <text key={i} x={x+18} y={250+i*16} fontSize="11" fill="#2E75B6" fontFamily="'Courier New',monospace">{ln}</text>
              ))}
            </g>
          );
        })()}

        {/* Related Rates panel */}
        {(() => {
          const x=458, w=428, h=240;
          return (
            <g>
              <rect x={x} y={62} width={w} height={h} rx={5} fill="#fff" stroke="#ccd9ea"/>
              <rect x={x+4} y={66} width={w-8} height={26} rx={3} fill="#c0392b"/>
              <text x={x+w/2} y={83} textAnchor="middle" fontSize="13" fill="#fff" fontWeight="700">Related Rates</text>

              {/* Strategy */}
              {[
                {y:104, step:'1', txt:'Identify all quantities changing with time', color:'#c0392b'},
                {y:128, step:'2', txt:'Write an equation relating them (geometry!)', color:'#c0392b'},
                {y:152, step:'3', txt:'Differentiate both sides w.r.t. time t', color:'#e07b00'},
                {y:176, step:'4', txt:'Substitute known rates and values', color:'#2E75B6'},
                {y:200, step:'5', txt:'Solve for the unknown rate', color:'#27ae60'},
              ].map(({y,step,txt,color}) => (
                <g key={step}>
                  <circle cx={x+22} cy={y-5} r={10} fill={color}/>
                  <text x={x+22} y={y-1} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{step}</text>
                  <text x={x+40} y={y} fontSize="12" fill="#333">{txt}</text>
                </g>
              ))}

              {/* Example: expanding circle */}
              <rect x={x+10} y={216} width={w-20} height={78} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
              <text x={x+18} y={232} fontSize="11" fill="#1F3864" fontWeight="700">Example:  Circle area A = πr²,  dr/dt = 2 cm/s</text>
              {[
                "Diff w.r.t. t:  dA/dt = 2πr · (dr/dt)",
                "At r = 5:  dA/dt = 2π(5)(2) = 20π cm²/s",
                "The area grows at 20π ≈ 62.8 cm² per second",
              ].map((ln,i) => (
                <text key={i} x={x+18} y={250+i*16} fontSize="11" fill="#c0392b" fontFamily="'Courier New',monospace">{ln}</text>
              ))}
            </g>
          );
        })()}
      </svg>
      <div style={S.dCaption}>
        Implicit differentiation extends the chain rule to equations where y cannot be isolated — just differentiate every term and attach dy/dx whenever y appears. Related rates apply implicit differentiation in time: every variable becomes a function of t, so every derivative gets a d/dt factor.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function Derivatives(props) {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Derivatives</h1>

      <p style={S.p}>
        The derivative measures how a function changes — its instantaneous rate of change at any point. Geometrically, it is the slope of the tangent line to a curve. In engineering and physics, derivatives appear everywhere: velocity is the derivative of position, acceleration is the derivative of velocity, and every optimization problem requires setting a derivative equal to zero to find a maximum or minimum.
      </p>

      <InfoBox
        title="Core Idea: The Derivative is an Instantaneous Rate of Change"
        items={[
          "Definition: f'(x) = lim[h→0] [f(x+h) − f(x)] / h  — the limit of the secant slope",
          "Notation: f'(x), dy/dx, Df, ẋ (Newton) — all mean the same thing",
          "Geometric meaning: slope of the tangent line to y = f(x) at the point (x, f(x))",
          "Physical meaning: instantaneous rate of change (velocity, current, growth rate)",
          "A function is differentiable at x if the limit exists — requires continuity but is stronger",
        ]}
      />

      <h2 style={S.h2}>The Limit Definition</h2>
      <DiagramLimitDefinition />
      <Table2 rows={[
        ["Definition",        "f'(a) = lim[h→0] [f(a+h)−f(a)] / h\nThe slope of the secant line as h shrinks to zero"],
        ["Secant slope",      "[f(a+h)−f(a)] / h = Δy/Δx\nAverage rate of change over the interval [a, a+h]"],
        ["Tangent slope",     "Limit of secant slope as h→0\nInstantaneous rate of change at x=a"],
        ["Differentiability", "f is differentiable at a if the limit exists and is finite\nRequires continuity; sharp corners and discontinuities are not differentiable"],
        ["One-sided limits",  "Left derivative: lim[h→0⁻]; Right derivative: lim[h→0⁺]\nDifferentiable only if both one-sided limits agree"],
      ]} />

      <h2 style={S.h2}>Basic Differentiation Rules</h2>
      <DiagramBasicRules />
      <Table2 rows={[
        ["Power rule",        "d/dx [xⁿ] = n·xⁿ⁻¹\nWorks for any real n (including fractions and negatives)"],
        ["Constant rule",     "d/dx [c] = 0\nA constant does not change — its derivative is always zero"],
        ["Constant multiple", "d/dx [c·f(x)] = c·f'(x)\nConstants factor out of the derivative operator"],
        ["Sum / difference",  "d/dx [f±g] = f'±g'\nDifferentiate term by term — linearity of the derivative"],
        ["Product rule",      "d/dx [f·g] = f'g + fg'\n'First d-second plus second d-first'"],
        ["Linearity",         "d/dx [af + bg] = a·f' + b·g'\nThe derivative is a linear operator — distributes over sums"],
      ]} />

      <h2 style={S.h2}>Chain Rule and Quotient Rule</h2>
      <DiagramChainQuotient />
      <Table2 rows={[
        ["Chain rule",        "d/dx [f(g(x))] = f'(g(x)) · g'(x)\nOuter derivative × inner derivative — outer evaluated at inner"],
        ["Chain rule (Leibniz)","dy/dx = (dy/du)·(du/dx)\nUseful when substituting u = g(x)"],
        ["Quotient rule",     "d/dx [f/g] = (f'g − fg') / g²\n'Low d-High minus High d-Low over Low squared'"],
        ["Quotient from product","Can always write f/g = f·g⁻¹ and use product + chain rules\nSometimes cleaner than the quotient rule formula"],
        ["Common chain mistakes","Forgetting to multiply by g'(x) — always check for inner derivatives\nExample: d/dx[sin(3x)] = cos(3x)·3, not just cos(3x)"],
      ]} />

      <h2 style={S.h2}>Derivatives of Standard Functions</h2>
      <DiagramStandardDerivatives />
      <Table2 rows={[
        ["Exponential eˣ",   "d/dx [eˣ] = eˣ — unique: it is its own derivative\nd/dx [aˣ] = aˣ·ln(a) for any base a > 0"],
        ["Natural log",      "d/dx [ln x] = 1/x  (x > 0)\nd/dx [ln|x|] = 1/x  (extends to x < 0)"],
        ["Trig derivatives", "sin→cos→−sin→−cos→sin (cycle of 4)\ntan→sec², cot→−csc², sec→sec·tan, csc→−csc·cot"],
        ["Inverse trig",     "d/dx [arcsin x] = 1/√(1−x²)\nd/dx [arctan x] = 1/(1+x²)  (very common in integrals)"],
        ["Log differentiation","Take ln of both sides, differentiate implicitly\nUseful for y = xˣ or products of many functions"],
      ]} />

      <h2 style={S.h2}>Geometric Meaning and Curve Sketching</h2>
      <DiagramGeometricMeaning />
      <Table2 rows={[
        ["f'(x) > 0",        "f is increasing on that interval\nTangent line has positive slope"],
        ["f'(x) < 0",        "f is decreasing on that interval\nTangent line has negative slope"],
        ["f'(x) = 0",        "Critical point — tangent is horizontal\nCandidate for local max, min, or saddle point"],
        ["f''(x) > 0",       "Concave up (∪ shape) — slope is increasing\nLocal minimum if f'=0 here (second derivative test)"],
        ["f''(x) < 0",       "Concave down (∩ shape) — slope is decreasing\nLocal maximum if f'=0 here"],
        ["Inflection point",  "f''=0 and concavity changes\nWhere the curve changes from ∪ to ∩ or vice versa"],
        ["First deriv test",  "Sign change of f' at critical point determines max/min\n+→−: local max;  −→+: local min;  no change: neither"],
      ]} />

      <h2 style={S.h2}>Implicit Differentiation and Related Rates</h2>
      <DiagramImplicitRelated />
      <Table2 rows={[
        ["Implicit diff.",    "Differentiate both sides w.r.t. x\nAttach dy/dx to every y-term (chain rule)"],
        ["Solving for dy/dx", "Collect all dy/dx terms on one side, factor, divide\nResult may contain both x and y"],
        ["Related rates",     "All variables are functions of time t\nDifferentiate the geometric equation w.r.t. t"],
        ["Common setups",     "Pythagoras: x²+y²=z² (ladder/shadow problems)\nVolume/area formulas: V=πr²h, A=πr²"],
        ["Notation tip",      "dx/dt = rate of change of x with respect to time\nUnits: (units of x) per (units of t)"],
        ["L'Hôpital's rule",  "If lim f/g = 0/0 or ∞/∞, then lim f/g = lim f'/g'\nApply derivatives to numerator and denominator separately"],
      ]} />

      <InfoBox
        title="Key Takeaway — How to Choose the Right Rule"
        items={[
          "Single power term (xⁿ)? → Power rule: d/dx[xⁿ] = n·xⁿ⁻¹",
          "Sum of terms? → Differentiate term by term (linearity)",
          "Two functions multiplied? → Product rule: f'g + fg'",
          "One function divided by another? → Quotient rule: (f'g − fg') / g²",
          "Function inside another function? → Chain rule: f'(g(x))·g'(x)",
          "y not isolated (implicit)? → Differentiate both sides, attach dy/dx to each y-term",
          "Quantities changing in time? → Related rates: differentiate w.r.t. t, substitute known rates",
          "Optimization? → Find f'(x)=0, then use first or second derivative test to classify",
        ]}
      />
    </div>
  );
}